import { useEffect, useState, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, BACKEND_URL } from "@/lib/api";
import { Loader2, MapPin, Camera, LogIn, LogOut as LogOutIcon, CheckCircle2, AlertTriangle } from "lucide-react";

export default function PunchIn() {
  const { user } = useAuth();
  const [emp, setEmp] = useState(null);
  const [locations, setLocations] = useState([]);
  const [today, setToday] = useState(null);
  const [pos, setPos] = useState(null);
  const [geoError, setGeoError] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [selfie, setSelfie] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [cameraOn, setCameraOn] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/employees", { params: { q: user.email } }).then((r) => {
      const found = r.data.find((e) => e.email === user.email) || r.data[0];
      setEmp(found || false);
      if (found) api.get(`/attendance/today/${found.id}`).then((x) => setToday(x.data || {}));
    });
    api.get("/locations").then((r) => setLocations(r.data));
  }, [user]);

  useEffect(() => {
    if (!navigator.geolocation) { setGeoError("Geolocation not supported"); return; }
    const id = navigator.geolocation.watchPosition(
      (p) => { setPos({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }); setGeoError(""); },
      (err) => setGeoError(err.message),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 5000 }
    );
    return () => navigator.geolocation.clearWatch(id);
  }, []);

  const startCamera = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" } });
      streamRef.current = s;
      if (videoRef.current) { videoRef.current.srcObject = s; videoRef.current.play(); }
      setCameraOn(true);
    } catch (e) {
      alert("Camera blocked: " + e.message);
    }
  };

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setCameraOn(false);
  };

  const capture = () => {
    if (!videoRef.current) return;
    const v = videoRef.current;
    const c = canvasRef.current;
    c.width = 320; c.height = 240;
    c.getContext("2d").drawImage(v, 0, 0, 320, 240);
    setSelfie(c.toDataURL("image/jpeg", 0.7));
    stopCamera();
  };

  useEffect(() => () => stopCamera(), []);

  const punch = async (action) => {
    if (!pos || !emp) return;
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/attendance/punch", {
        employee_id: emp.id,
        latitude: pos.lat, longitude: pos.lng,
        selfie_data: selfie,
        action,
      });
      setResult(data);
      setSelfie(null);
      const r = await api.get(`/attendance/today/${emp.id}`);
      setToday(r.data || {});
    } catch (e) {
      setResult({ error: e.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  if (emp === null) return <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  if (emp === false) return <div className="card-flat text-center"><p>No linked employee profile found.</p></div>;

  // distance to nearest location
  let nearest = null, minD = Infinity, inside = false;
  if (pos && locations.length) {
    for (const loc of locations) {
      const d = haversine(pos.lat, pos.lng, loc.latitude, loc.longitude);
      if (d < minD) { minD = d; nearest = loc; }
      if (d <= loc.radius_meters) inside = true;
    }
  }

  return (
    <div className="space-y-6 max-w-3xl mx-auto" data-testid="punch-page">
      <header>
        <div className="kpi-label" style={{ color: "var(--color-secondary)" }}>GPS Attendance</div>
        <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Punch In / Out</h1>
        <p className="text-stone-600 mt-1 text-sm">Captures your live location and verifies you're within an authorised geofence.</p>
      </header>

      <div className="card-flat">
        <div className="flex items-center gap-3">
          <img src={emp.photo_url} alt="" className="w-12 h-12 rounded-full object-cover" />
          <div className="flex-1"><div className="font-semibold">{emp.full_name}</div>
            <div className="text-xs text-stone-500">{emp.designation} · {emp.department_name}</div></div>
          <div className="text-right">
            <div className="kpi-label">Today</div>
            <div className="text-sm font-medium">{new Date().toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" })}</div>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-stone-100 grid grid-cols-2 gap-4 text-sm">
          <div>
            <div className="kpi-label">Punch In</div>
            <div className="font-bold text-lg mt-1" data-testid="today-in-time">{today?.in_time || "—"}</div>
            {today?.in_location && <div className="text-xs text-stone-500">📍 {today.in_location}</div>}
          </div>
          <div>
            <div className="kpi-label">Punch Out</div>
            <div className="font-bold text-lg mt-1" data-testid="today-out-time">{today?.out_time || "—"}</div>
            {today?.out_location && <div className="text-xs text-stone-500">📍 {today.out_location}</div>}
          </div>
        </div>
      </div>

      <div className="card-flat">
        <div className="flex items-center gap-2 mb-3">
          <MapPin className="w-4 h-4" style={{ color: "var(--color-primary)" }} />
          <div className="kpi-label">Your Location</div>
        </div>
        {geoError && <div className="text-sm text-red-700 bg-red-50 p-2 rounded">{geoError} — please enable location.</div>}
        {!pos && !geoError && <div className="text-sm text-stone-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> acquiring GPS…</div>}
        {pos && (
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Stat label="Latitude" v={pos.lat.toFixed(6)} />
            <Stat label="Longitude" v={pos.lng.toFixed(6)} />
            <Stat label="Accuracy" v={`±${pos.accuracy.toFixed(0)} m`} />
            {nearest && <Stat label={`Distance to ${nearest.name}`} v={`${minD.toFixed(0)} m`} />}
          </div>
        )}
        {pos && (
          <div className={`mt-3 px-3 py-2 rounded-md flex items-center gap-2 text-sm font-medium ${inside ? "bg-green-50 text-green-800" : "bg-orange-50 text-orange-800"}`}
               data-testid="geofence-status">
            {inside ? <><CheckCircle2 className="w-4 h-4" /> Inside authorised geofence</>
                    : <><AlertTriangle className="w-4 h-4" /> Outside geofence — attendance will be flagged</>}
          </div>
        )}
      </div>

      <div className="card-flat">
        <div className="flex items-center gap-2 mb-3"><Camera className="w-4 h-4" style={{ color: "var(--color-primary)" }} /><div className="kpi-label">Selfie Verification (Optional)</div></div>
        <canvas ref={canvasRef} className="hidden" />
        {selfie ? (
          <div className="flex items-center gap-3">
            <img src={selfie} alt="selfie" className="w-32 h-24 rounded-md object-cover ring-2 ring-primary/20" />
            <button onClick={() => { setSelfie(null); }} className="text-xs underline text-stone-600">Retake</button>
          </div>
        ) : cameraOn ? (
          <div className="flex items-center gap-3">
            <video ref={videoRef} className="w-40 h-30 rounded-md bg-stone-900" muted playsInline />
            <button onClick={capture} data-testid="capture-selfie-button"
                    className="px-3 py-2 rounded-md text-white text-sm font-medium" style={{ background: "var(--color-primary)" }}>
              Capture
            </button>
            <button onClick={stopCamera} className="px-3 py-2 rounded-md border border-stone-300 text-sm">Cancel</button>
          </div>
        ) : (
          <button onClick={startCamera} data-testid="start-camera-button"
                  className="text-sm px-3 py-2 border border-stone-300 rounded-md hover:bg-stone-50">
            Enable camera & take selfie
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <button onClick={() => punch("in")} disabled={!pos || busy || !!today?.in_time}
                data-testid="punch-in-button"
                className="py-3 rounded-md text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                style={{ background: "var(--color-primary)" }}>
          <LogIn className="w-4 h-4" /> {today?.in_time ? "Already Punched In" : "Punch In"}
        </button>
        <button onClick={() => punch("out")} disabled={!pos || busy || !today?.in_time || !!today?.out_time}
                data-testid="punch-out-button"
                className="py-3 rounded-md text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                style={{ background: "var(--color-secondary)" }}>
          <LogOutIcon className="w-4 h-4" /> {today?.out_time ? "Already Punched Out" : "Punch Out"}
        </button>
      </div>

      {result && !result.error && (
        <div className="card-flat" style={{ background: result.inside_fence ? "var(--color-primary-light)" : "#FCE7C9" }}
             data-testid="punch-result">
          <div className="kpi-label">Last Punch</div>
          <div className="mt-2 text-sm">
            {result.matched_location ? `✅ Verified at ${result.matched_location} (${result.distance_m}m from centre)` :
              `⚠️ Outside any geofence (nearest ${result.distance_m}m away)`}
          </div>
        </div>
      )}
      {result?.error && <div className="card-flat bg-red-50 text-red-800 text-sm">{result.error}</div>}
    </div>
  );
}

function Stat({ label, v }) {
  return <div><div className="kpi-label">{label}</div><div className="font-medium mt-0.5">{v}</div></div>;
}

function haversine(lat1, lon1, lat2, lon2) {
  const R = 6371000;
  const toRad = (x) => (x * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat/2)**2 + Math.cos(toRad(lat1))*Math.cos(toRad(lat2))*Math.sin(dLon/2)**2;
  return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
}
